import { Link } from "react-router-dom";
import { Award, ShieldCheck, Users, Target, Compass, ArrowUpRight } from "lucide-react";
import { Overline, SectionHead, FadeIn } from "@/components/site/Shared";

const TEAM_IMG = "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SITE_IMG = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=80";

const leadership = [
  { name: "Oinam Gitchandra", role: "Founder & Managing Director", bio: "A civil engineer with 25 years across residential, industrial and public works, Gitchandra founded OGK in 2008 on a single principle: bill only what is built." },
  { name: "Priya Venkatesan", role: "Director — Engineering", bio: "Chartered structural engineer leading our design studio. Priya's teams have certified over 4 million sq.ft of structures across seismic zones II–IV." },
  { name: "Arjun Mehta", role: "Director — Projects", bio: "Former PMC lead for two IT campuses, Arjun commands site operations, HSE systems and the 98% on-time record we defend fiercely." },
  { name: "Sofia D'Souza", role: "Head — Client Experience", bio: "Sofia built our client portal practice: transparent billing, weekly dashboards and a single point of contact for every project owner." },
];

const values = [
  { icon: Target, title: "Precision", desc: "Millimetre tolerances, measured quantities, engineered schedules. Approximation is not in our vocabulary." },
  { icon: ShieldCheck, title: "Integrity", desc: "Open books, honest bills, zero arbitration disputes in 18 years. We protect the client's rupee like our own." },
  { icon: Users, title: "People First", desc: "1,200 trained workers with PF, insurance and safety gear. Great buildings are built by people treated greatly." },
  { icon: Compass, title: "Stewardship", desc: "Water recycling, waste segregation and green-certified designs on every project we can influence." },
];

const certifications = [
  "ISO 9001:2015 — Quality Management",
  "ISO 45001:2018 — Occupational Health & Safety",
  "ISO 14001:2015 — Environmental Management",
  "Class-A Government Contractor Registration",
  "IGBC Member — Green Building Council",
  "CREDAI Member",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="py-24 sm:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">About OGK</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-4xl">
              Eighteen Years Of<br />Engineering <span className="text-ember">Trust</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              Founded in Imphal, Manipur in 2008, OGK Infra & Developers has grown from a three-person site team into a full-strength construction company delivering across the region — without ever losing the founder's habit of walking every site, every week.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 border-b border-line bg-graphite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <div className="img-zoom overflow-hidden border border-line">
              <img src={SITE_IMG} alt="OGK construction site" className="w-full h-[440px] object-cover" />
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <SectionHead overline="Our Story" title="Bill Only What Is Built" className="mb-8" />
            <p className="text-silver leading-relaxed">
              OGK began with a government road contract and a promise that seemed radical then: every bill would be measurable, every measurement verifiable. That promise became our operating system. Today, clients award us projects through transparent quotations, pay a modest advance, and then pay only against certified milestones — with photographs, measurements and test certificates attached to every invoice.
            </p>
            <p className="text-silver leading-relaxed mt-4">
              This discipline built more than buildings. It built repeat clients — over 70% of our order book comes from returning clients and their referrals — and it built an ERP-driven delivery culture where every daily progress report, material request and quality test lives in one system your project team can see.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Our Values" title="What We Refuse To Compromise" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {values.map((v, i) => (
              <FadeIn key={v.title} delay={i * 0.06}>
                <div className="bg-carbon p-8 h-full">
                  <v.icon size={26} className="text-ember mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-white uppercase tracking-tight">{v.title}</h3>
                  <p className="text-silver text-sm mt-3 leading-relaxed">{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-line bg-graphite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Leadership" title="The People Behind The Projects" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((m, i) => (
              <FadeIn key={m.name} delay={i * 0.06}>
                <div className="border border-line bg-carbon p-8 h-full lift" data-testid={`team-card-${i}`}>
                  <div className="w-14 h-14 bg-ember/15 border border-ember/40 flex items-center justify-center font-heading text-ember text-lg font-semibold mb-6">
                    {m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <h3 className="font-heading text-lg text-white uppercase tracking-tight">{m.name}</h3>
                  <p className="text-ember text-xs uppercase tracking-[0.15em] mt-1 font-bold">{m.role}</p>
                  <p className="text-silver text-sm mt-4 leading-relaxed">{m.bio}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionHead overline="Credentials" title="Certified. Audited. Accountable." className="mb-8" />
            <ul className="space-y-4">
              {certifications.map((c) => (
                <li key={c} className="flex items-center gap-4 text-white/90 text-sm border border-line bg-graphite px-6 py-4">
                  <Award size={18} className="text-ember shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="img-zoom overflow-hidden border border-line">
              <img src={TEAM_IMG} alt="OGK leadership team" className="w-full h-[440px] object-cover" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 bg-ember">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tighter text-white">Build With A Team You Can Audit</h2>
          <Link to="/request-quote" data-testid="about-cta-btn" className="bg-carbon text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 flex items-center gap-2 hover:bg-black transition-colors duration-200">
            Start The Conversation <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
